/**
 * Strictly match the prescription that corresponds to a specific uploaded
 * medication, by drug description or NDC. Used to prefill the Approve and
 * Follow-Up steps with the prescription the client just added, never a guess.
 *
 * When more than one prescription matches the same drug, the newest is returned:
 * prescriptionIds are Mongo ObjectIds whose leading hex encodes the creation time,
 * so the lexicographically-largest id is the most recently created.
 *
 * Returns "" when nothing matches; callers surface that (e.g. a Refresh affordance).
 */
export interface PrescriptionMatchTarget {
    drugDescription?: string;
    ndc?: string;
    excludePrescriptionId?: string;
}

const DOSAGE_FORM_ABBREVIATIONS: Record<string, string> = {
    tab: "tablet",
    tabs: "tablet",
    tablets: "tablet",
    cap: "capsule",
    caps: "capsule",
    capsules: "capsule",
};

const normalizeDescription = (s: string): string =>
    (s || "")
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .map((word) => DOSAGE_FORM_ABBREVIATIONS[word] || word)
        .join(" ");

// NDCs appear in different formats (e.g. "31722-0425-10" vs "31722042510").
const normalizeNdc = (s: string): string => (s || "").replace(/\D/g, "");

const newestId = (prescriptions: any[]): string =>
    prescriptions
        .map((rx) => rx.prescriptionId)
        .filter((id): id is string => !!id)
        .reduce((newest, id) => (id > newest ? id : newest), "");

export const matchPrescriptionId = (
    prescriptions: any[],
    target: PrescriptionMatchTarget
): string => {
    const targetDesc = normalizeDescription(target.drugDescription || "");
    const targetNdc = normalizeNdc(target.ndc || "");

    if (!targetDesc && !targetNdc) {
        return "";
    }

    const matches = (prescriptions || []).filter((rx) => {
        if (target.excludePrescriptionId && rx.prescriptionId === target.excludePrescriptionId) {
            return false;
        }
        const descMatch = !!targetDesc && normalizeDescription(rx.description) === targetDesc;
        const ndcMatch =
            !!targetNdc &&
            Array.isArray(rx.productDetails) &&
            rx.productDetails.some(
                (d: any) => typeof d === "string" && normalizeNdc(d) === targetNdc
            );
        return descMatch || ndcMatch;
    });

    return newestId(matches);
};
